import React, { useEffect, useState } from "react";
import "./manageImage.css";
import NavBar from "../../components/bar/navBar";
import { app } from "../../components/firebase/firebase";
import "firebase/compat/storage";
import Notification from "../../components/notification";
import AudioSource from "../../assets/sound/check.mp3";

// icons
import { RiSearchLine } from "react-icons/ri";
import ButtonComponent from "../../components/button";
import { formatDate } from "../../components/function";

const MAX_IMAGES = 10;

function ManageImage() {
  const [images, setImages] = useState([]);

  const [lastDoc, setLastDoc] = useState(null);

  const [loading, setLoading] = useState("none");
  const [btnDelete, setBtnDelete] = useState("disabled");

  const [selected, setSelected] = useState([]);

  const [notification, setNotification] = useState(false);

  const [change, setChange] = useState(1);

  const db = app.firestore();

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const querySnapshot = await db
          .collection("titoumr")
          .doc("public")
          .collection("images")
          .orderBy("date", "desc")
          .limit(MAX_IMAGES)
          .get();

        // Mise à jour du dernier document pour la pagination
        setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);

        // Ajout du champ id avec le nom du document
        const imagesData = querySnapshot.docs.map((doc) => ({
          ...doc.data(), // Données du document
          id: doc.id, // Nom du document
        }));

        setImages(imagesData);
      } catch (error) {
        console.error("Erreur lors de la récupération des données:", error);
      }
    };

    fetchImages();
  }, [change, db]);

  const handleClick = async () => {
    if (!lastDoc) {
      console.warn("Aucun document supplémentaire à charger.");
      return;
    }

    try {
      setLoading("loading");
      const querySnapshot = await db
        .collection("titoumr")
        .doc("public")
        .collection("images")
        .orderBy("date", "desc")
        .startAfter(lastDoc)
        .limit(MAX_IMAGES)
        .get();

      if (!querySnapshot.empty) {
        setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
        const imagesData = querySnapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));
        setImages((prevImages) => [...prevImages, ...imagesData]);
      } else {
        console.log("Aucune image supplémentaire à charger.");
      }
    } catch (error) {
      console.error("Erreur lors du chargement des images :", error);
    } finally {
      setLoading("none");
    }
  };

  const handleSelect = (id, event) => {
    const isSelected = selected.includes(id);
    let temp;

    if (isSelected) {
      temp = selected.filter((number) => number !== id);
    } else {
      temp = [...selected, id];
    }
    setSelected(temp);
    setBtnDelete(temp.length > 0 ? "none" : "disabled");
    const target = event.currentTarget;
    target.classList.toggle("active");
  };

  const handleDelete = async () => {
    setBtnDelete("loading");
    const failedDeletions = [];

    try {
      // Récupérer les promesses de suppression
      const deletePromises = selected.map(async (id) => {
        const imageToDelete = images.find((item) => item.id === id);

        if (!imageToDelete || !imageToDelete.fullPath) {
          console.error(
            `Image avec l'ID ${id} introuvable ou sans chemin valide.`
          );
          failedDeletions.push(id);
          return;
        }

        try {
          // Supprimer le document Firestore
          await db
            .collection("titoumr")
            .doc("public")
            .collection("images")
            .doc(id)
            .delete();

          // Supprimer le fichier dans Firebase Storage
          await app.storage().ref(imageToDelete.fullPath).delete();
        } catch (error) {
          console.error(
            `Erreur lors de la suppression de l'image ${id}:`,
            error
          );
          failedDeletions.push(id);
        }
      });

      // Attendre la fin de toutes les suppressions
      await Promise.all(deletePromises);

      // Mise à jour des images et des sélections après suppression réussie
      setImages((prevImages) =>
        prevImages.filter((image) => !selected.includes(image.id))
      );
      clearAllSelections();
      setBtnDelete("disabled");

      // Notification en cas de réussite partielle ou totale
      if (failedDeletions.length === 0) {
        setNotification(true);
        const audio = new Audio(AudioSource);
        audio.play();
        setTimeout(() => {
          setNotification(false);
        }, 3000);
      } else {
        console.warn(
          `Les suppressions suivantes ont échoué : ${failedDeletions.join(
            ", "
          )}`
        );
        alert(
          `Certaines images n'ont pas pu être supprimées : ${failedDeletions.join(
            ", "
          )}. Veuillez réessayer.`
        );
      }
    } catch (error) {
      console.error("Une erreur inattendue est survenue :", error);
      alert(
        "Une erreur inattendue est survenue pendant la suppression. Veuillez réessayer."
      );
    } finally {
      setBtnDelete("disabled");
    }
  };

  const handleSearch = (value) => {
    // Nettoyage des sélections actuelles
    clearAllSelections();

    // Suppression des espaces superflus
    const trimmedValue = value.trim();

    console.log("Recherche en cours pour :", trimmedValue);

    // Vérification de la longueur de la recherche
    if (trimmedValue.length >= 3) {
      // Vérification que la collection existe
      if (!db || !db.collection) {
        console.error("La base de données ou la collection est indisponible.");
        return;
      }

      const formattedDate = value;

      // Effectuer deux requêtes pour gérer le OU logique
      const keywordsQuery = db
        .collection("titoumr")
        .doc("public")
        .collection("images")
        .where("keywords", "array-contains", trimmedValue);

      const dateQuery = db
        .collection("titoumr")
        .doc("public")
        .collection("images")
        .where("dateCreated", "==", formattedDate);

      Promise.all([keywordsQuery.get(), dateQuery.get()])
        .then(([keywordsSnapshot, dateSnapshot]) => {
          setBtnDelete("disabled");

          // Récupérer les données des deux requêtes
          const keywordsData = keywordsSnapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }));

          const dateData = dateSnapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }));

          // Fusionner les résultats en supprimant les doublons
          const combinedData = Array.from(
            new Map(
              [...keywordsData, ...dateData].map((item) => [item.id, item])
            ).values()
          );

          // Mise à jour de l'état des images
          setImages(combinedData);
        })
        .catch((error) => {
          console.error(
            "Erreur lors de la récupération des documents dans la collection 'images' :",
            error
          );
        });
    } else {
      // Mise à jour de l'état si la recherche est trop courte
      console.warn("La recherche doit contenir au moins 3 caractères.");
      setChange((prevChange) => prevChange + 1);
    }
  };
  const clearAllSelections = () => {
    // Réinitialise la classe CSS des éléments sélectionnés
    selected.forEach((id) => {
      const element = document.getElementById(id);
      if (element) {
        element.classList.remove("active");
      }
    });

    // Réinitialise la liste des éléments sélectionnés
    setSelected([]);
  };

  return (
    <>
      <NavBar link="/" title="Administration" />
      <Notification state={notification} />
      <div className="ManageImage">
        <div className="header">
          <h1>Modifier ou supprimer</h1>
        </div>
        <div className="wrapper">
          <label htmlFor="search" className="ctn-search">
            <RiSearchLine className="icon" />
            <input
              type="search"
              name=""
              id="search"
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Rechercher par numéro d'image"
            />
          </label>
          <div className="result">
            <div className="top">
              <div className="left">{images.length} images</div>
              <div className="right">
                <ButtonComponent
                  text="Supprimer"
                  state={btnDelete}
                  color="red"
                  onClick={() => handleDelete()}
                />
              </div>
            </div>
            <div className="table">
              <table>
                <thead>
                  <tr>
                    <th></th>
                    <th>Preview</th>
                    <th>Name</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {images.map((data, index) => (
                    <tr
                      id={data.id}
                      onClick={(event) => handleSelect(data.id, event)}
                      key={index} // Utilisez key ici pour les identifiants React
                    >
                      <td>
                        <div className="ctn-input">
                          <div className="check"></div>
                        </div>
                      </td>
                      <td>
                        <img
                          src={data.link}
                          alt={data.name}
                          style={{ width: "100px" }}
                          loading="lazy"
                        />
                      </td>
                      <td>@{data.name}</td>
                      <td>{formatDate(data.date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="ctn-button">
              <ButtonComponent
                text="Charger plus"
                onClick={() => lastDoc && handleClick()} // Désactive le bouton si `lastDoc` est nul
                state={loading}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ManageImage;
